import { z } from 'zod';

import { localeSchema } from './catalog.js';

/** Схемы корзины и checkout — общие для API и витрины. */

const trimmed = (max: number) => z.string().trim().min(1).max(max);
const optionalTrimmed = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => v || undefined);

/** Индексы: LV-1234 / LT-12345 / 12345 (EE). Префикс страны добавляется при нормализации. */
const POSTAL_CODES: Record<string, RegExp> = {
  LV: /^(LV-?)?\d{4}$/i,
  LT: /^(LT-?)?\d{5}$/i,
  EE: /^\d{5}$/,
};

export function normalizePostalCode(countryCode: string, value: string): string {
  const compact = value.trim().toUpperCase().replace(/\s/g, '');
  if (countryCode === 'LV' || countryCode === 'LT') {
    return `${countryCode}-${compact.replace(/^(LV|LT)-?/, '')}`;
  }
  return compact;
}

export const addressSchema = z
  .object({
    firstName: trimmed(100),
    lastName: trimmed(100),
    company: optionalTrimmed(200),
    line1: trimmed(200),
    line2: optionalTrimmed(200),
    city: trimmed(100),
    postalCode: trimmed(16),
    countryCode: z
      .string()
      .length(2)
      .transform((v) => v.toUpperCase()),
    phone: z
      .string()
      .trim()
      .regex(/^\+?[0-9 ()-]{6,20}$/, 'Invalid phone number')
      .optional(),
  })
  .superRefine((a, ctx) => {
    const format = POSTAL_CODES[a.countryCode];
    // Шаблоны индексов — простые фиксированной длины, поле ограничено 16 символами.
    // nosemgrep: ajinabraham.njsscan.dos.regex_dos.regex_dos
    if (format && !format.test(a.postalCode.replace(/\s/g, ''))) {
      ctx.addIssue({ code: 'custom', path: ['postalCode'], message: 'Invalid postal code' });
    }
  })
  .transform((a) => ({ ...a, postalCode: normalizePostalCode(a.countryCode, a.postalCode) }));
export type Address = z.output<typeof addressSchema>;

export const createCartSchema = z.object({
  market: z.string().regex(/^[A-Za-z]{2}$/),
  locale: localeSchema,
});

export const addCartItemSchema = createCartSchema.extend({
  variantId: z.uuid(),
  qty: z.number().int().min(1).max(999),
});
export type AddCartItemRequest = z.infer<typeof addCartItemSchema>;

export const updateCartItemSchema = z.object({ qty: z.number().int().min(0).max(999) });

export const checkoutContactSchema = z.object({
  email: z.email().max(254),
  phone: z
    .string()
    .trim()
    .regex(/^\+?[0-9 ()-]{6,20}$/, 'Invalid phone number')
    .optional(),
  /** Согласие на письма о брошенной корзине и новостях — только по явной галочке. */
  marketingConsent: z.boolean().default(false),
});
export type CheckoutContactRequest = z.infer<typeof checkoutContactSchema>;

export const checkoutAddressSchema = z.object({
  shipping: addressSchema,
  /** Нет — совпадает с адресом доставки. */
  billing: addressSchema.optional(),
  /** Покупка на компанию: название обязательно, VAT ID — для reverse charge. */
  business: z
    .object({
      companyName: trimmed(200),
      regNumber: optionalTrimmed(64),
      vatId: optionalTrimmed(32),
    })
    .optional(),
});
export type CheckoutAddressRequest = z.input<typeof checkoutAddressSchema>;

export const checkoutShippingSchema = z.object({
  shippingMethodId: z.uuid(),
  pickupPointId: z.string().trim().min(1).max(64).optional(),
});
export type CheckoutShippingRequest = z.infer<typeof checkoutShippingSchema>;

/**
 * Ограничения метода по посылке (ячейка пакомата, пункт выдачи): размеры коробки — с
 * поворотом, вес и объём — всей посылки. Нет поля — нет ограничения.
 */
export const shippingLimitsSchema = z.object({
  maxWeightG: z.number().int().min(1).optional(),
  /** Наибольшая коробка: стороны в мм (порядок не важен — коробку можно повернуть). */
  maxBoxMm: z
    .tuple([z.number().int().min(1), z.number().int().min(1), z.number().int().min(1)])
    .optional(),
  /** Самая длинная сторона, мм (например, курьер — 1,2 м без надбавки). */
  maxLengthMm: z.number().int().min(1).optional(),
  maxVolumeCm3: z.number().int().min(1).optional(),
});
export type ShippingLimits = z.infer<typeof shippingLimitsSchema>;

/**
 * Как читать цены тарифа: `gross` — уже с НДС рынка (цена покупателя); `net` — тариф
 * перевозчика без НДС, к нему прибавляются топливная надбавка и наценка магазина,
 * затем НДС (настройка `shipping_pricing`).
 */
const bracketsSchema = z
  .array(z.object({ upToG: z.number().int().min(1), amountMinor: z.number().int().min(0) }))
  .min(1);

/**
 * Зона адреса доставки (например, Venipak: Рига / крупные города / остальная Латвия):
 * город покупателя начинается с одного из `cityPrefixes` (без диакритики, в нижнем
 * регистре) — действует сетка зоны. Не подошла ни одна — основная сетка `brackets`;
 * город ещё не введён — первая зона.
 */
export const shippingZoneSchema = z.object({
  name: z.string().min(1).max(64),
  cityPrefixes: z.array(z.string().min(2).max(64)).optional(),
  /** Почтовые индексы зоны (цифры без префикса страны): «1005» или диапазон «1000-1027». */
  postalCodes: z
    .array(z.string().regex(/^\d{3,6}(-\d{3,6})?$/))
    .max(2000)
    .optional(),
  brackets: bracketsSchema,
});
export type ShippingZone = z.infer<typeof shippingZoneSchema>;

const ruleBase = {
  basis: z.enum(['gross', 'net']).default('gross'),
  limits: shippingLimitsSchema.optional(),
  /**
   * Объёмный вес: см³ на 1 кг (DPD — 4000). Посылка считается по большему из
   * фактического и объёмного веса. Нет — только фактический вес.
   */
  volumetricDivisor: z.number().int().min(1).optional(),
  /** Доля топливной (энергетической) надбавки перевозчика: DPD Pickup в Балтии — 0,5. */
  fuelShare: z.number().min(0).max(1).optional(),
};

/**
 * Тариф метода доставки (`shipping_methods.price_rules`): фиксированная цена или сетка
 * по весу посылки, центы.
 */
export const shippingPriceRulesSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('flat'), amountMinor: z.number().int().min(0), ...ruleBase }),
  z.object({
    type: z.literal('weight'),
    brackets: bracketsSchema,
    /** Тяжелее последней ступени — метод недоступен, если не задано. */
    overMinor: z.number().int().min(0).optional(),
    zones: z.array(shippingZoneSchema).optional(),
    ...ruleBase,
  }),
]);
/** Вход (basis можно не указывать — «gross»). */
export type ShippingPriceRules = z.input<typeof shippingPriceRulesSchema>;
